import React, { useEffect, useState } from 'react';
import {
  ShortsMode,
  SHORTS_MODES,
  SHORTS_MODE_COPY,
  ReframeMode,
  REFRAME_MODES,
  REFRAME_MODE_COPY,
  LANGUAGE_OPTIONS,
  WhisperModelOption,
  FALLBACK_WHISPER_MODELS,
  SHORTS_TIP,
  whisperModelLabel,
} from '../utils/shorts';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/** Pre-selected in every transcription dropdown when the server has it installed; the server's
 *  own default (`defaultModel`) only applies when it does not. */
const DEFAULT_WHISPER_MODEL = 'large-v3';

/**
 * Loads GET /api/whisper-models once `enabled` turns true (a modal opening, a widget mounting) and
 * keeps the selection valid against whatever the server returns. Replaces three identical copies
 * of this effect that used to live in the shorts / caption entry points.
 */
export function useWhisperModels(enabled: boolean): {
  models: WhisperModelOption[];
  model: string;
  setModel: (name: string) => void;
} {
  const [models, setModels] = useState<WhisperModelOption[]>(FALLBACK_WHISPER_MODELS);
  const [model, setModel] = useState(DEFAULT_WHISPER_MODEL);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/whisper-models`, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to load Whisper models');

        const data = await res.json();
        const available: WhisperModelOption[] =
          Array.isArray(data.models) && data.models.length > 0 ? data.models : FALLBACK_WHISPER_MODELS;
        const serverDefault =
          typeof data.defaultModel === 'string' && data.defaultModel ? data.defaultModel : null;
        const preferred = [DEFAULT_WHISPER_MODEL, serverDefault].find(
          (name) => name && available.some((m) => m.name === name),
        );

        if (cancelled) return;
        setModels(available);
        setModel(preferred || available[0].name);
      } catch {
        if (cancelled) return;
        setModels(FALLBACK_WHISPER_MODELS);
        setModel(DEFAULT_WHISPER_MODEL);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { models, model, setModel };
}

interface InfoTipProps {
  text: string;
  /** Anchor the bubble to the icon's right edge when the label sits near the container's right side. */
  align?: 'left' | 'right';
}

/** A small circled "i" after a field label; its one-line explanation shows on hover or keyboard focus. */
export const InfoTip: React.FC<InfoTipProps> = ({ text, align = 'left' }) => (
  <button
    type="button"
    className={`info-tip${align === 'right' ? ' info-tip--right' : ''}`}
    aria-label={text}
    onClick={(e) => {
      // The tip lives on hover / focus. The click handler exists only so tapping the icon inside
      // a wrapping <label> doesn't toggle that label's control.
      e.preventDefault();
      e.stopPropagation();
    }}
  >
    i
    <span className="info-tip-bubble" role="tooltip">
      {text}
    </span>
  </button>
);

interface TranscriptionFieldsProps {
  language: string;
  onLanguageChange: (value: string) => void;
  model: string;
  onModelChange: (value: string) => void;
  models: WhisperModelOption[];
  /** Scope note shown under the pair. */
  helpText?: string;
}

/** The grouped "Transcription" section: Language + Transcription model, both feeding Whisper. */
export const TranscriptionFields: React.FC<TranscriptionFieldsProps> = ({
  language,
  onLanguageChange,
  model,
  onModelChange,
  models,
  helpText = 'Used only for this media job.',
}) => (
  <fieldset className="modal-group">
    <legend>Transcription</legend>
    <div className="cm-upload-select-grid">
      <div className="modal-field">
        <label>
          Language <InfoTip text={SHORTS_TIP.language} />
        </label>
        <select value={language} onChange={(e) => onLanguageChange(e.target.value)}>
          {LANGUAGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="modal-field">
        <label>
          Transcript <InfoTip text={SHORTS_TIP.model} align="right" />
        </label>
        <select value={model} onChange={(e) => onModelChange(e.target.value)}>
          {models.map((m) => (
            <option key={m.name} value={m.name}>
              {whisperModelLabel(m.name)}
            </option>
          ))}
        </select>
      </div>
    </div>
    {helpText ? <div className="cm-field-help">{helpText}</div> : null}
  </fieldset>
);

interface ShortsModePickerProps {
  value: ShortsMode;
  onChange: (mode: ShortsMode) => void;
  /** Radio-group name; give each mounted picker its own so they don't share selection. */
  name?: string;
  /** Lay the option rows out side-by-side instead of stacked. */
  horizontal?: boolean;
}

/** Classic / Studio choice, rendered as selectable option rows. */
export const ShortsModePicker: React.FC<ShortsModePickerProps> = ({
  value,
  onChange,
  name = 'shorts-mode',
  horizontal = false,
}) => (
  <div className="modal-field">
    <label>
      Shorts model <InfoTip text={SHORTS_TIP.shortsMode} />
    </label>
    <div className={`opt-row-group${horizontal ? ' opt-row-group--horizontal' : ''}`}>
      {SHORTS_MODES.map((m, i) => (
        <label key={m} className={`opt-row${value === m ? ' opt-row--active' : ''}`}>
          <input type="radio" name={name} checked={value === m} onChange={() => onChange(m)} />
          <span className="opt-row-body">
            <span className="opt-row-title">{SHORTS_MODE_COPY[m].label} <InfoTip text={SHORTS_MODE_COPY[m].help} align={horizontal && i > 0 ? 'right' : 'left'} /></span>
          </span>
        </label>
      ))}
    </div>
  </div>
);

interface ReframeModePickerProps {
  value: ReframeMode;
  onChange: (mode: ReframeMode) => void;
  /** Radio-group name; give each mounted picker its own so they don't share selection. */
  name?: string;
}

/** Active speaker / Fit all faces choice, rendered as selectable option rows. Applies to
 *  both Classic and Studio shorts generation. */
export const ReframeModePicker: React.FC<ReframeModePickerProps> = ({
  value,
  onChange,
  name = 'reframe-mode',
}) => (
  <div className="modal-field">
    <label>
      Reframe <InfoTip text={SHORTS_TIP.reframeMode} />
    </label>
    <div className="opt-row-group opt-row-group--horizontal">
      {REFRAME_MODES.map((m, i) => (
        <label key={m} className={`opt-row${value === m ? ' opt-row--active' : ''}`}>
          <input type="radio" name={name} checked={value === m} onChange={() => onChange(m)} />
          <span className="opt-row-body">
            <span className="opt-row-title">{REFRAME_MODE_COPY[m].label} <InfoTip text={REFRAME_MODE_COPY[m].help} align={i > 0 ? 'right' : 'left'} /></span>
          </span>
        </label>
      ))}
    </div>
  </div>
);

interface SummaryVideoOptionProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

/** The "Also create a summary video" toggle, in the same option-row style as the engine choice. */
export const SummaryVideoOption: React.FC<SummaryVideoOptionProps> = ({ checked, onChange }) => (
  <div className="modal-field">
    <label className={`opt-row${checked ? ' opt-row--active' : ''}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="opt-row-body">
        <span className="opt-row-title">
          Also create a summary video <InfoTip text={SHORTS_TIP.summary} />
        </span>
        <span className="opt-row-desc">
          Stitches the top moments into one recap short, in chronological order.
        </span>
      </span>
    </label>
  </div>
);
